import {
  Button,
  createStyles,
  Grid,
  Paper,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { Trans, useTranslations } from "adapters/i18n";
import { BannerAlert } from "features/shared/components";
import { usePrintPage } from "features/shared/hooks/usePrintPage";
import React, { useMemo, useRef, useState } from "react";
import { fetchLotWeightGainProjection, updateLots } from "redux/operations";
import type {
  EnrichedAnimalCategory,
  Goal,
  LivestockInfoFromFarm,
  ProjectedWeight,
  Retreat,
} from "shared/types";
import { useFilterForm } from "../hooks/useFilterForm";
import type { LotsFormRef, LotsPermissions } from "../lots.types";
import { getValueToUpdate } from "../utils/lots.utils";
import LotsFilterForm from "./lotsFilterForm";
import LotsListTable from "./lotsListTable/lotsListTable";
import WeightProjectionModal from "./weightProjectionModal";

type LotsPageProps = {
  enrichedAnimalCategories: EnrichedAnimalCategory[];
  lotGoals: Goal[];
  farmRetreats: Retreat[];
  fetchWeightGainProjection: (
    ...params: Parameters<typeof fetchLotWeightGainProjection>
  ) => Promise<void>;
  livestockInfosFromFarm: LivestockInfoFromFarm[];
  lotsPermissions: LotsPermissions;
  updateLots: (...params: Parameters<typeof updateLots>) => Promise<void>;
  weightGainProjection: ProjectedWeight | null;
} & WithStyles<typeof styles>;

function LotsPage({
  classes,
  enrichedAnimalCategories,
  farmRetreats,
  fetchWeightGainProjection,
  livestockInfosFromFarm,
  lotGoals,
  lotsPermissions,
  updateLots,
  weightGainProjection,
}: LotsPageProps) {
  const { t } = useTranslations();
  const lotsFormRef = useRef<LotsFormRef | null>(null);

  const [isWeightProjectionModalOpen, setIsWeightProjectionModalOpen] =
    useState(false);
  const [canSubmitForm, setCanSubmitForm] = useState(false);

  const { filterForm, handleChangeFilter, handleResetFilter, isFilterApplied } =
    useFilterForm();

  const handleToggleCanSubmitForm = (canSubmit: boolean) => {
    setCanSubmitForm(canSubmit);
  };

  const handleCloseModal = () => {
    setIsWeightProjectionModalOpen(false);
  };

  const handleOpenWeightProjectionDetails = async (
    row: LivestockInfoFromFarm
  ) => {
    await fetchWeightGainProjection(row.id);
    setIsWeightProjectionModalOpen(true);
  };

  const { handlePrintPage, ref: pageRef } = usePrintPage<HTMLDivElement>({});

  const handleSaveLots = async () => {
    const tableChanges = lotsFormRef.current?.getTableChanges() ?? {};

    if (!canSubmitForm) {
      return;
    }

    const changedLotIds = Object.keys(tableChanges).map((id) => Number(id));

    const livestockToUpdate = livestockInfosFromFarm
      .filter((lot) => changedLotIds.includes(lot.id))
      .map<Parameters<typeof updateLots>[0][number]>((lot) => ({
        goalId: getValueToUpdate(tableChanges[lot.id]?.goalId, lot.goalId),
        id: lot.id,
        goalWeight: {
          id: lot.goalWeight?.id ?? null,
          value: getValueToUpdate(
            tableChanges[lot.id]?.goalWeight,
            lot.goalWeight?.weight
          ),
        },
        measuredWeight: {
          id: lot.measuredWeight?.id ?? null,
          value: getValueToUpdate(
            tableChanges[lot.id]?.measuredWeight,
            lot.measuredWeight?.weight
          ),
          operationDate: getValueToUpdate(
            tableChanges[lot.id]?.weighingDate,
            lot.measuredWeight?.weightDate
          ),
        },
      }));

    await updateLots(livestockToUpdate);

    handleResetFilter();
  };

  return (
    <>
      <Paper className={classes.root} square={true}>
        <div ref={pageRef}>
          <Grid item sm={12} xs={12} className={classes.grid}>
            <Typography className={classes.header} gutterBottom={true}>
              {t("livestock:farm-lots-information")}
            </Typography>

            <div className={classes.filterContainer}>
              <LotsFilterForm
                farmRetreats={farmRetreats}
                filterForm={filterForm}
                onChangeFilter={handleChangeFilter}
                onPrint={handlePrintPage}
              />

              <div className={classes.saveButtonWrapper}>
                <SaveLotsButton
                  classes={classes}
                  isDisabled={!canSubmitForm}
                  onClick={handleSaveLots}
                />
              </div>
            </div>

            <BannerAlert type="info">
              <span>
                <Trans
                  i18nKey="livestock:lot-projected-weight-disclaimer"
                  components={{ bold: <strong /> }}
                />
              </span>
            </BannerAlert>

            <LotsListTable
              enrichedAnimalCategories={enrichedAnimalCategories}
              filterForm={filterForm}
              formRef={lotsFormRef}
              isFilterApplied={isFilterApplied}
              livestockInfosFromFarm={livestockInfosFromFarm}
              lotGoals={lotGoals}
              lotsPermissions={lotsPermissions}
              onCanSubmitFormChange={handleToggleCanSubmitForm}
              onOpenWeightProjectionDetails={handleOpenWeightProjectionDetails}
            />
          </Grid>
        </div>
      </Paper>
      <WeightProjectionModal
        isOpen={isWeightProjectionModalOpen}
        onClose={handleCloseModal}
        weightGainProjection={weightGainProjection}
      />
    </>
  );
}

type SaveLotsButtonProps = {
  isDisabled: boolean;
  onClick: () => void;
} & WithStyles<typeof styles>;

function SaveLotsButton({ classes, isDisabled, onClick }: SaveLotsButtonProps) {
  const { t } = useTranslations();

  return (
    <Button
      className={classes.saveButton}
      color="primary"
      data-testid="save-lots-button"
      disabled={isDisabled}
      onClick={onClick}
      variant="contained"
    >
      {t("common:actions.save")}
    </Button>
  );
}

const styles = () =>
  createStyles({
    root: {
      bottom: 0,
      height: "100%",
      minHeight: "calc(100vh - 90px)",
      overflowX: "auto",
      padding: "46px",
      top: "17%",
      width: "100%",
      zIndex: -1,
    },
    grid: {
      backgroundColor: "#fff",
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      gap: "16px",
      height: "100%",
      width: "100%",
      "@media print": {
        overflowX: "hidden",
        padding: "12px",
      },
    },
    header: {
      color: "#333333",
      fontSize: "20px",
      fontStretch: "medium",
      fontStyle: "medium",
      fontWeight: "bold",
      letterSpacing: "medium",
      lineHeight: "medium",
      marginBottom: 0,
      marginTop: "40px",
    },
    filterContainer: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      gap: "16px",
      justifyContent: "space-between",
      width: "100%",
    },
    saveButtonWrapper: {
      flexGrow: 1,
      display: "flex",
      justifyContent: "flex-end",
      "@media print": {
        display: "none",
      },
    },
    saveButton: {
      borderRadius: "18px",
    },
  });

export default withStyles(styles)(LotsPage);
